import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import { logError } from "@/lib/logger";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileUpload } from "../FileUpload";
import { Image, Plus, CheckSquare } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { SortableImageCard } from "./gallery/SortableImageCard";
import { EditImageDialog } from "./gallery/EditImageDialog";
import { HeroPicker } from "./gallery/HeroPicker";
import { HomePhotosPicker } from "./gallery/HomePhotosPicker";
import { BulkActionBar } from "./gallery/BulkActionBar";
import { useContent } from "@/lib/content";
import { displayUrl, CATEGORIES } from "./gallery/imageUrl";

const HERO_KEY = "hero_image";

export const GalleryManager = () => {
  const { token } = useAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [images, setImages] = useState([]);
  const [hero, setHero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ url: "", alt: "", category: "portraits" });
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const { refresh: refreshContent } = useContent();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchImages = useCallback(async () => {
    try {
      const [gallery, heroRes] = await Promise.all([
        axios.get(`${API}/public/gallery`),
        axios.get(`${API}/public/content/${HERO_KEY}`),
      ]);
      setImages(gallery.data);
      setHero(heroRes.data?.content ? { url: heroRes.data.content, ...(heroRes.data.metadata || {}) } : null);
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const saveHero = async (next) => {
    try {
      await axios.put(`${API}/admin/content/${HERO_KEY}`, {
        title: "Home Hero Image",
        content: next?.url || "",
        metadata: next ? { alt: next.alt, position: next.position || "top" } : null,
      }, authHeaders);
      setHero(next);
      refreshContent();
      toast.success(next ? "Home hero updated!" : "Hero reset to default");
    } catch (error) {
      toast.error("Failed to update hero image");
    }
  };

  const handleMultipleUpload = async (uploadedFiles) => {
    let added = 0;
    for (const file of uploadedFiles) {
      try {
        await axios.post(`${API}/admin/gallery`, { url: file.url, alt: file.original_name || "Gallery image", category: "portraits" }, authHeaders);
        added += 1;
      } catch (error) {
        logError(error);
      }
    }
    if (added === uploadedFiles.length) toast.success(`${added} images added to gallery!`);
    else toast.warning(`${added} of ${uploadedFiles.length} images added to gallery`);
    fetchImages();
  };

  const addImage = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/gallery`, formData, authHeaders);
      toast.success("Image added!");
      setShowForm(false);
      setFormData({ url: "", alt: "", category: "portraits" });
      fetchImages();
    } catch (error) {
      toast.error("Failed to add image");
    }
  };

  const updateImage = async (id, fields) => {
    try {
      const res = await axios.put(`${API}/admin/gallery/${id}`, fields, authHeaders);
      setImages((prev) => prev.map((img) => (img.id === id ? res.data : img)));
      if (hero?.url === res.data.url) saveHero({ ...hero, alt: res.data.alt });
      toast.success("Photo updated");
      setEditing(null);
    } catch (error) {
      toast.error("Failed to update photo");
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, authHeaders);
      toast.success("Image deleted");
      fetchImages();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleSelected = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const bulkAction = async (action, category) => {
    const ids = [...selected];
    if (action === "delete" && !window.confirm(`Delete ${ids.length} photo(s)? This cannot be undone.`)) return;
    try {
      const res = await axios.post(`${API}/admin/gallery/bulk`, { ids, action, category }, authHeaders);
      toast.success(action === "delete" ? `${res.data.count} photo(s) deleted` : `${res.data.count} photo(s) moved to ${category}`);
      exitSelectMode();
      fetchImages();
    } catch (error) {
      toast.error("Bulk action failed");
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = images.findIndex((i) => i.id === active.id);
    const newIndex = images.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(images, oldIndex, newIndex);
    setImages(reordered);
    try {
      await axios.put(`${API}/admin/gallery/reorder`, { ids: reordered.map((i) => i.id) }, authHeaders);
      toast.success("Order saved");
    } catch (error) {
      toast.error("Failed to save order");
      fetchImages();
    }
  };

  return (
    <div data-testid="admin-gallery-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-charcoal">Gallery Manager</h1>
          <p className="font-body text-stone-grey">Upload, caption, reorder and feature your photos</p>
        </div>
        <div className="flex gap-2">
          <Button variant={selectMode ? "default" : "outline"} onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))} data-testid="gallery-select-toggle">
            <CheckSquare className="w-4 h-4 mr-2" /> {selectMode ? "Done selecting" : "Select"}
          </Button>
          <Button onClick={() => setShowForm(!showForm)} className="btn-primary" data-testid="gallery-add-image-toggle">
            <Plus className="w-4 h-4 mr-2" /> Add Image
          </Button>
        </div>
      </div>

      <HomePhotosPicker />

      <HeroPicker hero={hero} onPositionChange={(position) => saveHero({ ...hero, position })} onReset={() => saveHero(null)} />

      <div className="glass rounded-xl p-6 mb-8">
        <h3 className="font-heading text-lg text-charcoal mb-4">Quick Upload</h3>
        <p className="font-body text-sm text-stone-grey mb-4">Upload multiple images at once - they will be added directly to your gallery</p>
        <FileUpload onUpload={handleMultipleUpload} multiple={true} label="Drop images here or click to upload multiple" />
      </div>

      {showForm && (
        <div className="glass rounded-xl p-6 mb-8">
          <h3 className="font-heading text-lg text-charcoal mb-4">Add Single Image</h3>
          <form onSubmit={addImage} className="space-y-4">
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Upload from Device</Label>
              <FileUpload onUpload={(url) => setFormData({ ...formData, url })} label="Upload image from your device" />
            </div>
            <div className="text-center font-body text-sm text-taupe">— OR —</div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Image URL (paste a link)</Label>
              <Input value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://example.com/image.jpg" className="form-input" />
            </div>
            {formData.url && (
              <div className="border rounded-lg p-2 bg-sand/20">
                <img src={displayUrl(formData.url)} alt="Preview" className="max-h-40 mx-auto rounded" />
              </div>
            )}
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Alt Text / Description</Label>
              <Input value={formData.alt} onChange={(e) => setFormData({ ...formData, alt: e.target.value })} placeholder="Portrait in black dress" required className="form-input" />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Category</Label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="form-input w-full">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="btn-primary" disabled={!formData.url}>Add Image</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : images.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <Image className="w-12 h-12 text-taupe mx-auto mb-4" />
          <p className="font-body text-stone-grey">No images yet. Upload your first photos!</p>
        </div>
      ) : (
        <>
          <p className="font-body text-xs text-taupe mb-3" data-testid="gallery-reorder-hint">Drag the grip handle on any photo to reorder. The order is saved automatically.</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
                {images.map((img) => (
                  <SortableImageCard key={img.id} img={img} isHero={hero?.url === img.url} onEdit={setEditing} onDelete={deleteImage}
                    selectMode={selectMode} selected={selected.has(img.id)} onToggle={() => toggleSelected(img.id)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </>
      )}

      {selectMode && <BulkActionBar count={selected.size} onMove={(cat) => bulkAction("move", cat)} onDelete={() => bulkAction("delete")} onSelectAll={() => setSelected(new Set(images.map((i) => i.id)))} onClear={() => setSelected(new Set())} />}

      <EditImageDialog
        image={editing}
        isHero={!!editing && hero?.url === editing.url}
        onClose={() => setEditing(null)}
        onSave={updateImage}
        onSetHero={(img) => { saveHero({ url: img.url, alt: img.alt, position: "top" }); setEditing(null); }}
      />
    </div>
  );
};
